<template>
  <div>
    <q-select
      v-model="selectedMapping"
      :options="filteredMappings"
      option-label="display_label"
      option-value="id"
      :label="label"
      outlined
      dense
      use-input
      input-debounce="300"
      clearable
      :loading="loading"
      :rules="required ? [val => !!val || 'Unit is required'] : []"
      @filter="filterUnits"
      @update:model-value="handleSelect"
    >
      <template #option="scope">
        <q-item v-bind="scope.itemProps">
          <q-item-section>
            <q-item-label>{{ scope.opt.qudt_label }}</q-item-label>
            <q-item-label caption>
              {{ scope.opt.qudt_symbol || scope.opt.unit_string }} ({{ scope.opt.quantity_kind }})
            </q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-select>

    <div v-if="selectedMapping" class="text-caption text-grey-7 q-mt-xs">
      QUDT: {{ selectedMapping.qudt_uri }}
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { authenticatedFetch } from '@/utils/apiHelper';

  const props = defineProps({
    modelValue: {
      type: Number,
      default: null,
    },
    quantityKind: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: 'Unit',
    },
    required: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['update:modelValue']);

  const $q = useQuasar();
  const allMappings = ref([]);
  const filteredMappings = ref([]);
  const selectedMapping = ref(null);
  const loading = ref(false);

  onMounted(async () => {
    await loadUnitMappings();
  });

  watch(() => props.modelValue, (newVal) => {
    if (newVal && allMappings.value.length > 0) {
      selectedMapping.value = allMappings.value.find((m) => m.id === newVal);
    } else {
      selectedMapping.value = null;
    }
  }, { immediate: true });

  watch(() => props.quantityKind, () => {
    filterByQuantityKind();
  });

  async function loadUnitMappings() {
    loading.value = true;
    try {
      const url = props.quantityKind
        ? `/api/v2/unit-mappings?quantity_kind=${props.quantityKind}`
        : '/api/v2/unit-mappings';

      const response = await authenticatedFetch(url);

      if (!response.ok) {
        throw new Error('Failed to load unit mappings');
      }

      const data = await response.json();
      // Deduplicate by qudt_uri to avoid showing aliases in dropdown
      const uniqueByQudtUri = {};
      data.data.forEach((m) => {
        // Keep the first entry for each qudt_uri (prefer one with symbol)
        if (!uniqueByQudtUri[m.qudt_uri] ||
          (m.qudt_symbol && !uniqueByQudtUri[m.qudt_uri].qudt_symbol)) {
          uniqueByQudtUri[m.qudt_uri] = m;
        }
      });

      allMappings.value = Object.values(uniqueByQudtUri).map((m) => ({
        ...m,
        display_label: `${m.qudt_symbol || m.unit_string} - ${m.qudt_label}`,
      }));

      filterByQuantityKind();

      // Set initial selection if modelValue is provided
      if (props.modelValue) {
        selectedMapping.value = allMappings.value.find((m) => m.id === props.modelValue);
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load unit mappings',
        caption: error.message,
      });
    } finally {
      loading.value = false;
    }
  }

  function filterByQuantityKind() {
    if (props.quantityKind) {
      filteredMappings.value = allMappings.value.filter((m) =>
        m.quantity_kind === props.quantityKind,
      );
    } else {
      filteredMappings.value = allMappings.value;
    }
  }

  function filterUnits(val, update) {
    update(() => {
      if (val === '') {
        filterByQuantityKind();
      } else {
        const needle = val.toLowerCase();
        const baseList = props.quantityKind
          ? allMappings.value.filter((m) => m.quantity_kind === props.quantityKind)
          : allMappings.value;

        filteredMappings.value = baseList.filter((m) =>
          m.unit_string.toLowerCase().includes(needle) ||
          m.qudt_label.toLowerCase().includes(needle) ||
          (m.qudt_symbol && m.qudt_symbol.toLowerCase().includes(needle)),
        );
      }
    });
  }

  function handleSelect(mapping) {
    emit('update:modelValue', mapping?.id || null);
  }
</script>